"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_modal_1 = __importDefault(require("react-modal"));
const styled_components_1 = __importDefault(require("styled-components"));
const compliance_pool_types_1 = require("../compliance-pool-types");
const useFetch_1 = __importDefault(require("../../../hooks/useFetch"));
const apiUtils_1 = require("../../../util/apiUtils");
const theme_1 = require("../../../theme");
const common_styled_components_1 = require("../../common-styled-components");
const TransactionAttachmentUploader_1 = __importDefault(require("../common/TransactionAttachments/TransactionAttachmentUploader"));
const transactionAttachmentUtil_1 = require("./util/transactionAttachmentUtil");
const amountUtils_1 = require("../../../util/amountUtils");
const TransactionAttachmentTable_1 = require("../common/tables/TransactionAttachmentTable");
const maxAttachmentFileSize = 30 * 1024 * 1024 - 500;
// ─── Styled components ──────────────────────────────────────────────────────
const Card = styled_components_1.default.div `
  background-color: white;
  border: 1px solid ${theme_1.theme.colors.grey(4)};
  border-radius: 8px;
  padding: ${theme_1.theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(3)};
`;
const CardHeader = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(0)};
`;
const ShipName = styled_components_1.default.div `
  ${theme_1.theme.fontTitle};
  ${theme_1.theme.fontSize(0)};
  color: ${theme_1.theme.colors.blackText};
  font-weight: 600;
`;
const ShipMeta = styled_components_1.default.div `
  ${theme_1.theme.fontSize(-1)};
  color: ${theme_1.theme.colors.grey(2)};
`;
const BalanceBadge = styled_components_1.default.div `
  display: inline-block;
  margin-top: ${theme_1.theme.spacing(1)};
  padding: ${theme_1.theme.spacing(0)} ${theme_1.theme.spacing(1)};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${({ $positive }) => $positive ? theme_1.theme.colors.green + "22" : theme_1.theme.colors.red + "22"};
  color: ${({ $positive }) => $positive ? theme_1.theme.colors.green : theme_1.theme.colors.red};
  border: 1px solid
    ${({ $positive }) => $positive ? theme_1.theme.colors.green + "55" : theme_1.theme.colors.red + "55"};
`;
const DropZones = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(2)};
`;
const DropZone = styled_components_1.default.div `
  border: 2px dashed
    ${({ $isDragging }) => $isDragging ? theme_1.theme.colors.blue : theme_1.theme.colors.grey(3)};
  border-radius: 8px;
  padding: ${theme_1.theme.spacing(2)} ${theme_1.theme.spacing(2)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme_1.theme.spacing(1)};
  text-align: center;
  background-color: ${({ $isDragging }) => $isDragging ? theme_1.theme.colors.blue + "0d" : theme_1.theme.colors.grey(6)};
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  min-height: 80px;

  &:hover {
    border-color: ${theme_1.theme.colors.blue};
    background-color: ${theme_1.theme.colors.blue + "0d"};
  }
`;
const DropZoneLabel = styled_components_1.default.span `
  ${theme_1.theme.fontSize(-1)};
  font-weight: 600;
  color: ${theme_1.theme.colors.blackText};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
const DropZoneCount = styled_components_1.default.span `
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ $hasFiles }) => $hasFiles ? theme_1.theme.colors.blue : theme_1.theme.colors.grey(3)};
`;
const DropZoneHint = styled_components_1.default.span `
  font-size: 0.7rem;
  color: ${theme_1.theme.colors.grey(2)};
`;
const DropZoneError = styled_components_1.default.span `
  font-size: 0.7rem;
  color: ${theme_1.theme.colors.red};
`;
const DropZoneActions = styled_components_1.default.div `
  display: flex;
  gap: ${theme_1.theme.spacing(1)};
  margin-top: ${theme_1.theme.spacing(1)};
`;
const tableModalStyles = {
    ...common_styled_components_1.baseReactModalStyles,
    content: {
        ...common_styled_components_1.baseReactModalStyles.content,
        width: "95vw",
        maxWidth: "1140px",
        maxHeight: "85vh",
    },
};
const AttachmentDropZone = ({ attachmentType, count, transactionAttachments, shipId, selectedYear, periodicalStatementOptions, refresh, }) => {
    const [isDragging, setIsDragging] = (0, react_1.useState)(false);
    const [isUploaderOpen, setIsUploaderOpen] = (0, react_1.useState)(false);
    const [isTableOpen, setIsTableOpen] = (0, react_1.useState)(false);
    const [droppedFiles, setDroppedFiles] = (0, react_1.useState)([]);
    const [error, setError] = (0, react_1.useState)(null);
    const dragCounter = (0, react_1.useRef)(0);
    const openUploader = (files = []) => {
        setDroppedFiles(files);
        setIsUploaderOpen(true);
    };
    const handleDragEnter = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        dragCounter.current++;
        setIsDragging(true);
    }, []);
    const handleDragOver = (0, react_1.useCallback)((e) => {
        e.preventDefault();
    }, []);
    const handleDragLeave = (0, react_1.useCallback)(() => {
        dragCounter.current--;
        if (dragCounter.current === 0)
            setIsDragging(false);
    }, []);
    const handleDrop = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length === 0)
            return;
        const accepted = files.filter((f) => f.size <= maxAttachmentFileSize);
        const rejected = files.filter((f) => f.size > maxAttachmentFileSize);
        if (rejected.length > 0) {
            setError(`Skipped ${rejected.length} oversized file${rejected.length === 1 ? "" : "s"}`);
        }
        else {
            setError(null);
        }
        if (accepted.length > 0)
            openUploader(accepted);
    }, []);
    const label = transactionAttachmentUtil_1.ATTACHMENT_TYPE_LABELS[attachmentType];
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(TransactionAttachmentUploader_1.default, { shipId: shipId, attachmentType: attachmentType, selectedYear: selectedYear, initialFiles: droppedFiles, isOpen: isUploaderOpen, periodicalStatementOptions: periodicalStatementOptions, onClose: () => {
                    setIsUploaderOpen(false);
                    setDroppedFiles([]);
                }, onOpen: () => openUploader(), refresh: refresh }), (0, jsx_runtime_1.jsx)(react_modal_1.default, { isOpen: isTableOpen, onRequestClose: () => setIsTableOpen(false), contentLabel: `Show ${label.plural}`, style: tableModalStyles, children: (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { children: label.plural }), (0, jsx_runtime_1.jsx)(TransactionAttachmentTable_1.TransactionAttachmentTable, { transactionAttachments: transactionAttachments, attachmentType: attachmentType, selectedYear: selectedYear, shipId: shipId, periodicalStatementOptions: periodicalStatementOptions, refresh: refresh, isInAdminTools: true }), (0, jsx_runtime_1.jsx)("div", { style: { marginTop: theme_1.theme.spacing(3), textAlign: "right" }, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { onClick: () => setIsTableOpen(false), children: "Close" }) })] }) }), (0, jsx_runtime_1.jsxs)(DropZone, { "$isDragging": isDragging, onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onClick: () => openUploader(), children: [(0, jsx_runtime_1.jsx)(DropZoneLabel, { children: label.plural }), (0, jsx_runtime_1.jsx)(DropZoneCount, { "$hasFiles": count > 0, children: count }), error ? ((0, jsx_runtime_1.jsx)(DropZoneError, { children: error })) : ((0, jsx_runtime_1.jsx)(DropZoneHint, { children: "Drop or click to add" })), (0, jsx_runtime_1.jsx)(DropZoneActions, { children: (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: (e) => {
                                e.stopPropagation();
                                setIsTableOpen(true);
                            }, children: "Show" }) })] })] }));
};
const PoolShipCard = ({ ship, selectedYear }) => {
    const [attachments, _setUrl, setBody, refresh] = (0, useFetch_1.default)("admin/get_transaction_attachments.json", undefined, true);
    (0, react_1.useEffect)(() => {
        setBody(JSON.stringify({ ship_id: ship.id, year: selectedYear }));
    }, [ship.id, selectedYear, setBody]);
    const attachmentData = attachments.status === apiUtils_1.LoaderStatus.Success ? attachments.data : [];
    const psAttachments = attachmentData.filter(compliance_pool_types_1.isPeriodicalStatement);
    const bdnAttachments = attachmentData.filter(compliance_pool_types_1.isBunkerDeliveryNote);
    const psCount = psAttachments.length;
    const bdnCount = bdnAttachments.length;
    const periodicalStatementOptions = psAttachments.map((ps) => ({
        value: ps.id,
        label: ps.name,
    }));
    const balance = ship.intensity?.adjusted_emission_balance_ton;
    return ((0, jsx_runtime_1.jsxs)(Card, { children: [(0, jsx_runtime_1.jsxs)(CardHeader, { children: [(0, jsx_runtime_1.jsx)(ShipName, { children: ship.name }), (0, jsx_runtime_1.jsxs)(ShipMeta, { children: ["IMO: ", ship.imo] }), balance !== undefined && ((0, jsx_runtime_1.jsxs)(BalanceBadge, { "$positive": balance >= 0, children: ["Balance: ", (0, amountUtils_1.formatNumber)(balance, 0), " tCO\u2082e"] }))] }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(2) }, children: "Documents" }), (0, jsx_runtime_1.jsxs)(DropZones, { children: [(0, jsx_runtime_1.jsx)(AttachmentDropZone, { attachmentType: 1, count: psCount, transactionAttachments: psAttachments, shipId: ship.id, selectedYear: selectedYear, refresh: refresh }), (0, jsx_runtime_1.jsx)(AttachmentDropZone, { attachmentType: 2, count: bdnCount, transactionAttachments: bdnAttachments, shipId: ship.id, selectedYear: selectedYear, periodicalStatementOptions: periodicalStatementOptions, refresh: refresh })] })] })] }));
};
exports.default = PoolShipCard;
